import React from "react"
import type { UseFormReturn } from "react-hook-form"

import { NotificationType } from "@/schemas/notification"
import useCompanyPaymentTypes from "@/screens/CompanySettings/Accounting/hooks/useCompanyPaymentTypes"
import type { PaymentMethodFormValues } from "@/screens/CompanySettings/Accounting/schemas/PaymentMethodSchema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UsePaymentMethodDialogProps {
  methods: UseFormReturn<PaymentMethodFormValues>
  companyId: string
  onSuccess: () => void
  onClose: () => void
}

interface UsePaymentMethodDialogReturnProps {
  isCancelConfirmationOpen: boolean
  setIsCancelConfirmationOpen: React.Dispatch<React.SetStateAction<boolean>>
  canSubmit: boolean
  handleSubmit: () => void
  handleCancel: () => void
}

export const usePaymentMethodDialog = ({
  methods,
  companyId,
  onSuccess,
  onClose,
}: UsePaymentMethodDialogProps): UsePaymentMethodDialogReturnProps => {
  const { createPaymentType } = useCompanyPaymentTypes({ companyId, pause: true })
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = React.useState(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const {
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const canSubmit = isValid && isDirty && !isSubmitting

  const handleFormSubmit = React.useCallback(
    async (data: PaymentMethodFormValues) => {
      try {
        const result = await createPaymentType({
          input: {
            companyId: data.companyId,
            locationIds: [data.locationId],
            method: data.method,
            accountType: data.accountType,
            bankId: data.bankId || undefined,
            bankLocationId: data.bankLocationId || undefined,
            swiftCode: data.swiftCode || undefined,
            routingNumber: data.routingNumber || undefined,
            iban: data.iban || undefined,
            institutionNumber: data.institutionNumber || undefined,
            accountNumber: data.accountNumber || undefined,
          },
        })

        if (result.data?.companyPaymentType?.create?.__typename === "CompanyPaymentTypeCreateSuccess") {
          onSuccess()
          notify({
            type: NotificationType.Success,
            silent: false,
            message: "Payment type created successfully",
          })
        } else {
          const errorMessage =
            result.data?.companyPaymentType?.create?.__typename === "CompanyPaymentTypeFailure"
              ? result.data.companyPaymentType.create.error?.message || "Failed to create payment type"
              : "Failed to create payment type"

          notify({
            type: NotificationType.Error,
            silent: false,
            message: errorMessage,
          })
        }
      } catch {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "An unexpected error occurred. Please try again.",
        })
      }
    },
    [createPaymentType, onSuccess, notify]
  )

  const handleCancel = React.useCallback(() => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleFormSubmitWrapper = React.useCallback(() => {
    handleSubmit(handleFormSubmit)()
  }, [handleSubmit, handleFormSubmit])

  return {
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
    canSubmit,
    handleSubmit: handleFormSubmitWrapper,
    handleCancel,
  }
}

export default usePaymentMethodDialog
